import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import {
  Wrench, Search, ShieldCheck, Car, CreditCard, Layers, ChevronRight,
  Phone, MessageCircle, PhoneCall, ClipboardList, FileCheck, Hammer,
  BadgeCheck, Timer, Banknote, ChevronDown
} from 'lucide-react';
import HeroSection from '../components/home/HeroSection';
import FeaturedVehicles from '../components/home/FeaturedVehicles';
import ReviewsBanner from '../components/home/ReviewsBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceIcons = {
  'Réparation Automobile': Wrench,
  'Diagnostic Automobile': Search,
  'Contrôle Technique': ShieldCheck,
  'Vente de Pièces Auto': Layers,
  'Plaques d\'Immatriculation': CreditCard,
  'Accessoires Automobile': Car,
};

const marqueeItems = [
  'GARAGE PROFESSIONNEL', 'QUAROUBLE (59)', 'VÉHICULES CERTIFIÉS',
  'DEVIS GRATUIT', 'PIÈCES DE QUALITÉ', '10+ ANS D\'EXPÉRIENCE', 'DIAGNOSTIC COMPLET',
];

const steps = [
  { icon: PhoneCall, num: '01', title: 'Appelez-nous', desc: 'Prenez rendez-vous par téléphone ou via le formulaire de contact.' },
  { icon: ClipboardList, num: '02', title: 'Diagnostic', desc: 'Nous effectuons un diagnostic complet et détaillé de votre véhicule.' },
  { icon: FileCheck, num: '03', title: 'Devis gratuit', desc: 'Vous recevez un devis clair et transparent avant toute intervention.' },
  { icon: Hammer, num: '04', title: 'Intervention', desc: 'Nos mécaniciens qualifiés réalisent les travaux avec soin et efficacité.' },
];

const guarantees = [
  { icon: BadgeCheck, title: 'Garantie pièces et main d\'oeuvre', desc: 'Toutes nos interventions sont garanties.' },
  { icon: Timer, title: 'Rapidité d\'intervention', desc: 'Délais respectés, véhicule prêt à la date convenue.' },
  { icon: Banknote, title: 'Prix justes et transparents', desc: 'Pas de coûts cachés. Le prix du devis est le prix final.' },
  { icon: ShieldCheck, title: 'Véhicules révisés et contrôlés', desc: 'Chaque véhicule d\'occasion est contrôlé point par point.' },
];

const carBrands = [
  'Peugeot', 'Renault', 'Citroën', 'Volkswagen', 'Audi', 'BMW',
  'Mercedes', 'Ford', 'Opel', 'Toyota', 'Fiat', 'Dacia',
  'Nissan', 'Hyundai', 'Kia', 'Seat', 'Skoda',
];

const faqs = [
  { q: 'Prenez-vous rendez-vous ?', a: 'Oui, vous pouvez prendre rendez-vous par téléphone au 06 13 85 19 46 ou via notre formulaire de contact. Nous essayons de vous recevoir dans les meilleurs délais.' },
  { q: 'Le devis est-il gratuit ?', a: 'Oui, le devis est toujours gratuit et sans engagement. Nous vous détaillons chaque intervention et son coût avant de commencer les travaux.' },
  { q: 'Quelles marques de véhicules réparez-vous ?', a: 'Nous intervenons sur toutes les marques : Peugeot, Renault, Citroën, Volkswagen, BMW, Mercedes, Audi, Ford, Opel, Toyota, et bien d\'autres.' },
  { q: 'Proposez-vous une garantie sur les réparations ?', a: 'Oui, toutes nos interventions sont garanties pièces et main d\'œuvre. Les véhicules d\'occasion sont également contrôlés et révisés avant la vente.' },
  { q: 'Quels sont vos moyens de paiement acceptés ?', a: 'Nous acceptons les paiements par carte bancaire, espèces et virement. Un paiement en plusieurs fois peut être envisagé selon le montant.' },
  { q: 'Où êtes-vous situés ?', a: 'Nous sommes situés au 32 Rue Marcellin Berthelot, 59243 Quarouble, proche de Valenciennes, Onnaing et Fresnes-sur-Escaut.' },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="text-white font-semibold text-sm lg:text-base pr-4 group-hover:text-[#DC2626] transition-colors">{item.q}</span>
        <ChevronDown size={18} className={`text-zinc-500 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-[#DC2626]' : ''}`} />
      </button>
      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="pb-5">
          <p className="text-zinc-500 text-sm leading-relaxed">{item.a}</p>
        </motion.div>
      )}
    </div>
  );
}

const Home = () => {
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vRes, sRes] = await Promise.all([
          axios.get(`${API_URL}/api/vehicles`),
          axios.get(`${API_URL}/api/services`),
        ]);
        const available = vRes.data.filter(v => v.featured || v.status !== 'vendu').slice(0, 6);
        setVehicles(available);
        setServices(sRes.data.slice(0, 6));
      } catch (err) {
        console.error('Error loading home data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)' }} data-testid="home-page">
      <HeroSection />

      {/* Marquee Band */}
      <div className="border-y border-white/5 py-4" style={{ background: 'var(--bg-surface)' }}>
        <Marquee speed={40} gradient={false}>
          {marqueeItems.map((item, i) => (
            <span key={i} className="mx-8 text-xs font-semibold tracking-[0.2em] text-zinc-500 uppercase" style={{ fontFamily: 'var(--font-mono)' }}>
              {item} <span className="text-[#DC2626] mx-4">///</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Services Section */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--bg-primary)' }} data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="section-label">Nos prestations</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4 tracking-tight">
              Des services complets pour votre auto
            </h2>
            <p className="text-zinc-500 max-w-lg">De la réparation au diagnostic, en passant par la vente de pièces. Tout pour votre véhicule.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => {
              const Icon = serviceIcons[service.name] || Wrench;
              const isMain = index === 0;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`service-card group ${isMain ? 'md:col-span-2 lg:row-span-2' : ''}`}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className={`flex items-center justify-center w-12 h-12 mb-4 border border-white/10 ${isMain ? 'bg-[#DC2626]' : 'bg-white/5'}`}>
                    <Icon size={22} className={isMain ? 'text-white' : 'text-[#DC2626]'} />
                  </div>
                  <h3 className={`font-bold text-white mb-2 ${isMain ? 'text-2xl' : 'text-lg'}`}>{service.name}</h3>
                  <p className={`text-zinc-500 leading-relaxed ${isMain ? 'text-base' : 'text-sm'}`}>{service.description}</p>
                  {service.features && (
                    <ul className="mt-4 space-y-2">
                      {service.features.slice(0, isMain ? 4 : 2).map((f, fi) => (
                        <li key={fi} className="flex items-center gap-2 text-sm text-zinc-400">
                          <span className="w-1 h-1 bg-[#DC2626] rounded-full flex-shrink-0"></span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold text-zinc-400 hover:text-white transition-colors" data-testid="home-services-link">
              Voir tous nos services <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 border-t border-white/5" style={{ background: 'var(--bg-surface)' }} data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="section-label">Comment ça marche</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 tracking-tight">
              Simple, rapide et transparent
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative text-center p-6"
                data-testid={`step-${i}`}
              >
                <div className="text-5xl font-black text-white/[0.03] absolute top-2 left-1/2 -translate-x-1/2" style={{ fontFamily: 'var(--font-mono)' }}>{step.num}</div>
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#DC2626]/10 border border-[#DC2626]/20 mb-5 relative z-10">
                  <step.icon size={24} className="text-[#DC2626]" />
                </div>
                <h3 className="text-white font-bold mb-2">{step.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.desc}</p>
                {i < 3 && <div className="hidden lg:block absolute top-1/3 -right-3 w-6 h-[1px] bg-white/10"></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <FeaturedVehicles vehicles={vehicles} />

      {/* Guarantees */}
      <section className="py-20 lg:py-28 border-t border-white/5" style={{ background: 'var(--bg-primary)' }} data-testid="guarantees-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="section-label">Nos engagements</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 tracking-tight">
              Pourquoi choisir MCA CAR ?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {guarantees.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="border border-white/5 p-6 hover:border-[#DC2626]/30 transition-all duration-300"
                style={{ background: 'var(--bg-surface)' }}
                data-testid={`guarantee-${i}`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#DC2626]/10 border border-[#DC2626]/20 mb-4">
                  <g.icon size={22} className="text-[#DC2626]" />
                </div>
                <h3 className="text-white font-bold text-base mb-2">{g.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{g.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Car Brands */}
      <section className="py-12 border-t border-b border-white/5" style={{ background: 'var(--bg-surface)' }} data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-xs text-zinc-600 uppercase tracking-[0.15em] mb-8" style={{ fontFamily: 'var(--font-mono)' }}>Toutes marques prises en charge</p>
          <Marquee speed={25} gradient={false}>
            {carBrands.map((brand, i) => (
              <span key={i} className="mx-8 text-lg font-bold text-zinc-700 hover:text-zinc-400 transition-colors cursor-default">
                {brand}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Reviews */}
      <ReviewsBanner />

      {/* FAQ */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--bg-surface)' }} data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="section-label">Questions fréquentes</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 tracking-tight">
              Besoin d'informations ?
            </h2>
          </motion.div>
          <div>
            {faqs.map((faq, i) => <FAQItem key={i} item={faq} />)}
          </div>
          <div className="text-center mt-10">
            <p className="text-zinc-500 text-sm mb-4">Vous avez d'autres questions ?</p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="faq-contact-btn">
              Contactez-nous <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 border-t border-white/5" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="border border-white/5 p-10 lg:p-16 relative overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#DC2626]/5 blur-3xl"></div>
            <div className="relative z-10 max-w-2xl">
              <span className="section-label">Prenez rendez-vous</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mt-3 mb-4 tracking-tight">
                Un projet ? Une question ?<br />Contactez-nous maintenant.
              </h2>
              <p className="text-zinc-500 mb-8">Devis gratuit et sans engagement. Notre équipe vous répond dans les plus brefs délais.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="cta-contact">
                  <MessageCircle size={16} /> Demander un devis
                </Link>
                <a href="tel:+33613851946" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/20 text-white font-semibold text-sm transition-all hover:bg-white/5" data-testid="cta-phone">
                  <Phone size={16} /> 06 13 85 19 46
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Float */}
      <a href="https://wa.me/33613851946" target="_blank" rel="noopener noreferrer" className="whatsapp-float" data-testid="whatsapp-button">
        <MessageCircle size={28} />
      </a>

      {/* Mobile Sticky CTA Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 flex" style={{ background: 'var(--bg-primary)' }}>
        <a href="tel:+33613851946" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm" data-testid="mobile-call-bar">
          <Phone size={16} /> Appeler
        </a>
        <a href="https://wa.me/33613851946" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-semibold text-sm" data-testid="mobile-whatsapp-bar">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>
  );
};

export default Home;
